import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BillingBanner from "@/components/BillingBanner";
import {
  Trophy, LayoutDashboard, Users, BarChart3, Target, Award,
  CreditCard, LogOut, Flame, Plus, Activity, MessageSquare, Database, Settings,
  Calendar as CalendarIcon, Lock, UserCircle, Camera, Upload, Swords, GraduationCap, Home, ShieldCheck,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FeedbackWidget from "@/components/FeedbackWidget";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { UserAvatar } from "@/components/UserAvatar";
import AvatarUploadDialog from "@/components/AvatarUploadDialog";
import MobileBottomNav from "@/components/MobileBottomNav";
import LegacyPanel from "@/components/LegacyPanel";
import { DEFAULT_ACCENT } from "@/lib/accent";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PosterStripes, PosterGrain, PosterRibbon } from "@/components/PosterFX";
import { ENABLED_DASHBOARD_ROUTES, PORTAL_HOME } from "@/lib/webAccess";
import { planDisplay } from "@/lib/plan";

const NAV_ITEMS = [
  { to: "/dashboard/home", icon: Home, label: "Home", testid: "nav-home" },
  { to: "/dashboard/overview", icon: LayoutDashboard, label: "Headquarters", testid: "nav-overview" },
  { to: "/dashboard/leaderboard", icon: Trophy, label: "Leaderboard", testid: "nav-leaderboard" },
  { to: "/dashboard/chat", icon: MessageSquare, label: "The Pit", testid: "nav-chat" },
  { to: "/dashboard/teams-arena", icon: Swords, label: "Arena", testid: "nav-arena", featureFlag: "arena" },
  { to: "/dashboard/sales", icon: BarChart3, label: "Sales Log", testid: "nav-sales" },
  { to: "/dashboard/training", icon: GraduationCap, label: "Training", testid: "nav-training" },
  { to: "/dashboard/itinerary", icon: CalendarIcon, label: "Itinerary", testid: "nav-itinerary", roles: ["owner"], ownerOnly: true },
  { to: "/dashboard/employees", icon: Users, label: "Employees", testid: "nav-employees", roles: ["owner", "manager"], ownerOnly: true },
  { to: "/dashboard/settings", icon: Settings, label: "Settings", testid: "nav-settings", roles: ["owner"], ownerOnly: true },
  { to: "/dashboard/activity", icon: Activity, label: "Activity Feed", testid: "nav-activity", roles: ["owner"], ownerOnly: true },
  { to: "/dashboard/integrations", icon: Database, label: "Integrations", testid: "nav-integrations", roles: ["owner"], ownerOnly: true },
  { to: "/dashboard/import-sales", icon: Upload, label: "Import Sales", testid: "nav-import-sales", roles: ["owner"], ownerOnly: true },
  { to: "/dashboard/sales-review", icon: ShieldCheck, label: "Sales Review", testid: "nav-sales-review", roles: ["owner"], ownerOnly: true },
  { to: "/dashboard/billing", icon: CreditCard, label: "Billing", testid: "nav-billing", roles: ["owner"], ownerOnly: true },
];

/** Two-tone rising chime via Web Audio API — no asset required. */
let _audioCtx = null;
function playChallengePing() {
  try {
    if (!_audioCtx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      _audioCtx = new AC();
    }
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    [
      { freq: 587.33, start: 0, dur: 0.12 },   // D5
      { freq: 880.00, start: 0.12, dur: 0.18 }, // A5
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
  } catch { /* browsers may block audio until user interaction */ }
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [arenaInbox, setArenaInbox] = useState({ pending_for_me: 0, active_for_me: 0 });

  useEffect(() => {
    api.get("/companies/me").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  // Poll arena inbox for the nav badge + ping sound
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/arena/inbox-count");
        if (cancelled) return;
        setArenaInbox((prev) => {
          // Play a subtle two-tone ping when pending count increases
          if (r.data.pending_for_me > prev.pending_for_me) {
            try { playChallengePing(); } catch { /* audio blocked in some browsers */ }
          }
          return r.data;
        });
      } catch { /* silent — non-blocking */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const items = NAV_ITEMS
    .filter((i) => !i.roles || i.roles.includes(user?.role))
    .filter((i) => ENABLED_DASHBOARD_ROUTES.has(i.to));
  const featureFlags = company?.feature_flags || [];
  const isFeatureLocked = (item) => item.featureFlag && !featureFlags.includes(item.featureFlag);

  // Everything except the portal Home lands in the mobile-web "More" sheet.
  const overflowItems = items.filter((i) => i.to !== PORTAL_HOME);

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-[#0A0A0A] border-r border-[#27272A]">
      <Link to="/" className="flex items-center gap-3 px-6 h-24 border-b border-[#27272A]" data-testid="dashboard-brand">
        <img src="/app-icon.png" alt="SalesArena HQ" className="w-[45px] h-[45px] rounded-xl object-cover flex-shrink-0 border border-[#27272A]" data-testid="dashboard-brand-logo" />
        <span className="font-logo text-[16.8px] whitespace-nowrap tracking-tight">SALESARENA<span className="text-[#178AE5]"> HQ</span></span>
      </Link>
      <div className="px-4 py-4 border-b border-[#27272A]">
        <div className="font-label text-[#A1A1AA] text-[10px]">COMPANY</div>
        <div className="flex items-center gap-2 mt-1 min-w-0">
          {company?.logo_url && (
            <img src={company.logo_url} alt="" className="w-8 h-8 object-contain rounded-sm bg-[#0A0A0A] flex-shrink-0" data-testid="sidebar-company-logo" />
          )}
          <div
            className={`font-display brand-text min-w-0 break-words leading-tight ${
              (company?.name || "").length > 26 ? "text-sm" : (company?.name || "").length > 16 ? "text-base" : "text-lg"
            }`}
            data-testid="company-name"
          >
            {company?.name || "—"}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xs font-label px-2 py-0.5 bg-[#121212] border border-[#27272A] rounded-sm text-[#A1A1AA]" data-testid="sidebar-plan-label">{planDisplay(company)}</span>
          {company?.subscription_status === "past_due" && (
            <span className="text-xs font-label px-2 py-0.5 bg-[#FFC832]/10 border border-[#FFC832]/40 rounded-sm text-[#FFC832]" data-testid="sidebar-past-due">PAST DUE</span>
          )}
          {company?.subscription_status === "canceled" && (
            <span className="text-xs font-label px-2 py-0.5 bg-[#E13C32]/10 border border-[#E13C32]/40 rounded-sm text-[#E13C32]">CANCELED</span>
          )}
          <span className="text-xs text-[#A1A1AA]">{company?.current_users || 0}/{company?.max_users ?? "∞"} users</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {items.map((item) => {
          const locked = isFeatureLocked(item);
          if (locked) {
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => {
                  if (user?.role === "owner") {
                    navigate("/dashboard/billing");
                    toast.message("Upgrade your plan to unlock the Arena");
                  } else {
                    toast.message("Ask your owner to upgrade the plan to unlock the Arena");
                  }
                }}
                data-testid={`${item.testid}-locked`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm font-label text-sm transition-colors hover:bg-[#FFC832]/5"
                style={{ color: "rgba(255,200,50,0.72)" }}
                title="Pro feature — click to upgrade"
              >
                <item.icon
                  className="w-4 h-4 shrink-0"
                  style={{ filter: "drop-shadow(0 0 4px rgba(255,200,50,0.35))" }}
                />
                <span className="flex-1 text-left">{item.label}</span>
                <span
                  data-testid={`${item.testid}-pro-ribbon`}
                  className="font-label uppercase leading-none text-[9px] px-1.5 py-[3px]"
                  style={{
                    color: "#0A0A0A",
                    background: "linear-gradient(180deg, #FFE07A 0%, #FFC832 55%, #C89614 100%)",
                    border: "1px solid rgba(255,224,122,0.9)",
                    clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)",
                    boxShadow: "0 0 8px rgba(255,200,50,0.45), inset 0 1px 0 rgba(255,255,255,0.5)",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    paddingBottom: "6px",
                  }}
                >
                  PRO
                </span>
              </button>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm font-label text-sm transition-colors ${
                  isActive
                    ? "brand-active"
                    : "text-[#A1A1AA] hover:text-white hover:bg-[#121212]"
                }`
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/dashboard/teams-arena" && (arenaInbox.pending_for_me > 0 || arenaInbox.active_for_me > 0) ? (
                <span
                  className={`text-[9px] font-label tracking-wider px-1.5 py-0.5 rounded-sm border ${
                    arenaInbox.pending_for_me > 0
                      ? "bg-[#E13C32]/15 text-[#E13C32] border-[#E13C32]/40 animate-pulse"
                      : "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30"
                  }`}
                  title={`${arenaInbox.pending_for_me} pending, ${arenaInbox.active_for_me} live`}
                  data-testid="arena-inbox-badge"
                >
                  {arenaInbox.pending_for_me > 0 ? arenaInbox.pending_for_me : "LIVE"}
                </span>
              ) : null}
              {item.preview && (
                <span className="text-[9px] font-label tracking-wider px-1.5 py-0.5 rounded-sm border border-[#FFC832] text-[#FFC832] bg-[#FFC832]/10">
                  PREVIEW
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[#27272A]">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 p-2 hover:bg-[#121212] rounded-sm group" data-testid="user-menu">
              <div className="relative">
                <UserAvatar
                  name={user?.name}
                  avatarUrl={user?.avatar_url}
                  initials={user?.avatar_initials}
                  size="md"
                  testid="sidebar-user-avatar"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#178AE5] border-2 border-[#0A0A0A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-2 h-2 text-white" />
                </div>
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#121212] border-[#27272A] text-white w-56">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#27272A]" />
            <DropdownMenuItem
              data-testid="menu-my-legacy"
              onClick={() => setLegacyOpen(true)}
              className="cursor-pointer hover:bg-[#0A0A0A] focus:bg-[#0A0A0A] text-white"
            >
              <Award className="w-4 h-4 mr-2" /> My Legacy
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-my-profile"
              onClick={() => navigate(PORTAL_HOME)}
              className="cursor-pointer hover:bg-[#0A0A0A] focus:bg-[#0A0A0A] text-white"
            >
              <UserCircle className="w-4 h-4 mr-2" /> Account Home
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-change-picture"
              onClick={() => setAvatarDialogOpen(true)}
              className="cursor-pointer hover:bg-[#0A0A0A] focus:bg-[#0A0A0A] text-white"
            >
              <Camera className="w-4 h-4 mr-2" /> {user?.avatar_url ? "Change picture" : "Upload picture"}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#27272A]" />
            <DropdownMenuItem
              data-testid="logout-button"
              onClick={async () => { await logout(); navigate("/"); toast.success("Logged out"); }}
              className="cursor-pointer hover:bg-[#0A0A0A] focus:bg-[#0A0A0A] text-white"
            >
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div
      className="flex flex-col h-screen bg-[#0A0A0A] text-white"
      style={{ "--brand": company?.accent_color || "#178AE5" }}
      data-testid="dashboard-root"
    >
      <ImpersonationBanner />
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <SidebarContent />
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile top bar — brand only. Navigation + logout live in the bottom bar. */}
          <header className="lg:hidden relative flex items-center justify-center h-14 px-4 border-b border-[#27272A] bg-[#0A0A0A]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#178AE5] flex items-center justify-center rounded-sm">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <span className="font-logo whitespace-nowrap">SALESARENA HQ</span>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom,0px)+80px)] lg:pb-0">
            <BillingBanner company={company} role={user?.role} />
            <Outlet />
          </main>
        </div>
      </div>
      {/* Mobile-only bottom navigation */}
      <MobileBottomNav
        overflowItems={overflowItems}
        isFeatureLocked={isFeatureLocked}
        arenaInbox={arenaInbox}
      />
      {["owner", "manager"].includes(user?.role) ? <FeedbackWidget /> : null}
      <AvatarUploadDialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen} />
      <Dialog open={legacyOpen} onOpenChange={setLegacyOpen}>
        <DialogContent
          className="text-white max-w-3xl max-h-[85vh] overflow-y-auto p-0 border-2"
          style={{
            backgroundColor: "#0A0A0A",
            borderColor: "#FFC832bb",
            backgroundImage: "linear-gradient(160deg, rgba(255,200,50,0.14), transparent 55%)",
            boxShadow: "0 0 30px rgba(255,200,50,0.35), inset 0 0 24px rgba(0,0,0,0.55)",
          }}
          data-testid="layout-legacy-dialog"
        >
          <PosterStripes color="#FFC832" size={110} opacity={0.75} />
          <PosterGrain opacity={0.05} />
          <PosterRibbon color="#FFC832">★ LEGACY ★</PosterRibbon>
          <DialogTitle className="sr-only">Legacy</DialogTitle>
          <div className="relative px-4 sm:px-5 pb-5 pt-2">
            {user && legacyOpen && <LegacyPanel userId={user.id} accent={user?.accent_color || DEFAULT_ACCENT} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid, kicker }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8" data-testid={testid}>
      <div>
        {kicker && (
          <div className="inline-flex items-center gap-2 mb-3 px-2 py-0.5 border border-[#E13C32]/50 bg-[#E13C32]/10 rounded-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E13C32] animate-pulse" />
            <span className="font-label text-[9px] tracking-[0.4em] text-[#E46B5A]">{kicker}</span>
          </div>
        )}
        <h1 className="font-display text-3xl md:text-5xl leading-tight">{title}</h1>
        {subtitle && <p className="text-[#A1A1AA] mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

/** Compact banner shown on owner-only pages so the business owner knows employees
 *  do NOT see the page at all. */
export function OwnerOnlyBanner({ note, testid = "owner-only-banner" }) {
  return (
    <div
      className="flex items-center gap-2 mb-4 px-3 py-2 rounded-sm bg-[#E13C32]/10 border border-[#E13C32]/30 text-[#E13C32]"
      data-testid={testid}
    >
      <Lock className="w-3.5 h-3.5 flex-shrink-0" />
      <div className="font-label text-[11px] tracking-wider uppercase">Owner access only</div>
      {note && (
        <div className="text-xs text-[#A1A1AA] normal-case tracking-normal ml-1">— {note}</div>
      )}
    </div>
  );
}
